/**
 * @email kpozos@ibl.mx
 * @author Karen Yunuen Pozos Arias
 * @jobTitle Full Stack Developer
 * @hook VisitorTypeTableApp
 * @description Custom hook to manage state and logic for the Approval system.
 * Handles local states for selection, searching, modals, permissions,
 * and dispatches Redux actions to fetch and delete approval data.
 *
 * @param {function} setDeleteModal - Setter for deleteModal state.
 * @param {Array} selectedList - Array of selected visitor type IDs.
 * @param {function} setSelectedList - Setter for selectedList state.
 *
 * @returns {Object} Exposed states and handlers for the Approval UI.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TABLES } from "../../../../../Apis/Tables";
import { SimpleSearch } from "../../../../../reduxToolkit/Search/SearchApi";
import { SEARCH_MODEL } from "../../../../../components/table/Model/TableModel";
import { VIEW_TYPE } from "../../../../../enums/ViewType";
import TableFunction from "../../../../../components/table/TableFunction";
import { GetVisitorTypeById } from "../../../../../reduxToolkit/VisitorTypes/VisitorTypesApi";
import { DeleteIconsOptions, UpdateIconsOptions } from "../../../../../components/buttons/IconButtons";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import routes from "../../../../../Routes/routes";
import { permissionObj } from "../../../../../Helpers/permission";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import RenderExpandedRow from "../SubComponents/RenderExpandedRow";
import {DEFAULT_VISITOR_TYPE} from "../Model/Model";

const VisitorTypeTableApp = ({ setDeleteModal, selectedList, setSelectedList, modified, setModified }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Flag indicating if app is running in development mode
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    // User permissions retrieved from Redux
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    // State to track currently expanded table row ID
    const [expandedRowId, setExpandedRowId] = useState(null);
    // State to track if all rows are selected
    const [isAllChecked, setIsAllChecked] = useState(false);
    // Flag to indicate if search is active
    const [search, setSearch] = useState(false);
    // Store data for export purposes
    const [bodyForExport, setBodyForExport] = useState('');
    // Search input value
    const [searchElement, setSearchElement] = useState('');
    // Flag for simple search mode
    const [simpleSearch, setSimpleSearch] = useState(false);
    const searchId = 'txt_af_search_visitor_types'; // Search input element ID
    const option = TABLES.VISITOR_TYPES; // Table option constant
    // State holding the fetched visitor types data
    const [results, setResults] = useState([]);
    // Contractor and supplier IDs from session storage
    const [contractorId, setContractorId] = useState(sessionStorage.getItem('contractorId'));
    const [supplierId, setSupplierId] = useState(sessionStorage.getItem('providerId'));

    /**
     * Renders the options dropdown menu for each visitor type row.
     * Shows update and delete options based on permissions.
     *
     * @param {Object} item - Visitor type data for the row.
     * @param {number} index - Index of the row.
     * @returns {JSX.Element} Dropdown menu component.
     */
    const renderOptionsMenu = (item, index) => {
        if(item?.disabled) return;
        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item.id}`} // Unique menu id per item
            >
                {(DEV || permission.includes(permissionObj?.WEB_VISITOR_TYPE_UPDATE)) && (
                    <DropDownMenuItem
                        id={"btn_vtta_update_" + item?.id}
                        index={index}
                        handleClose={handleClose}
                        name={t('update')}
                        click={() => {
                            navigate(routes.visitorType.update, { state: { id: item.id } });
                        }}
                        icon={<UpdateIconsOptions />}
                    />
                )}

                {(DEV || permission.includes(permissionObj?.WEB_VISITOR_TYPE_DELETE)) && (
                    <DropDownMenuItem
                        id={"btn_vtta_delete_" + item?.id}
                        index={index}
                        handleClose={handleClose}
                        name={t('delete')}
                        click={() => {
                            setSelectedList([item?.id]);
                            setDeleteModal(true);
                        }}
                        icon={<DeleteIconsOptions />}
                        fontColor={"font-color-error-600"}
                        iconColor={"icon-color-error-code-600"}
                    />
                )}
            </DropDownMenuProfile>
        );
    };

    /**
     * Handles expanding a table row to show more details.
     * Fetches data for the expanded visitor type if it is different from current.
     *
     * @param {Object} row - Row data object.
     */
    const handleRowExpand = (row) => {
        if (row?.id !== expandedRowId) {
            dispatch(GetVisitorTypeById(row?.id));
            setExpandedRowId(row?.id);
        }
    };

    // Table columns definition
    const columns = [
        { field: "name", label: "visitor_type" }
    ];

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({ page, size: rowsPerPage, model: option, key: searchElement });
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success === true) {
                setResults({
                    ...data,
                    content: data?.content
                });
            }
        });
    }
    // Using TableFunction hook to manage table functionalities
    const {
        t,
        handleCheckboxChange,
        handleDeleteAll,
        pagination,
        page,
        rowsPerPage,
        openMenu,
        handleMenuToggle,
        handleClose
    } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        labelRowsPerPage: 'visitor_types_per_page',
        hasContainer: true,
        fixedPagination: false,
        setIsAllChecked,
        searchElement,
        view: VIEW_TYPE.GRID.name,

        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Renders expanded content for a table row.
     *
     * @param {Object} row - Row data.
     * @returns {JSX.Element} Expanded row content.
     */
    const renderExpandedRow = ({row}) => {
        return <RenderExpandedRow data={row} />;
    };

    return {
        t,
        setIsAllChecked,
        DEV,
        permission,
        selectedList,
        columns,
        results,
        setResults,
        searchElement,
        setSearchElement,
        search,
        setSearch,
        option,
        searchId,
        setSelectedList,
        page,
        rowsPerPage,
        simpleSearch,
        setSimpleSearch,
        bodyForExport,
        setBodyForExport,
        contractorId,
        supplierId,
        pagination,
        handleCheckboxChange,
        handleDeleteAll,
        renderOptionsMenu,
        handleRowExpand,
        renderExpandedRow
    };
};

export default VisitorTypeTableApp;
